import type { ReactNode } from 'react';
import { Icon } from '../../primitives/icon/index.ts';
import { Text } from '../../primitives/Text.tsx';
import { VisuallyHidden } from '../../primitives/VisuallyHidden.tsx';
import styles from './DataTable.module.css';

/**
 * Design system § 14 — Data table, and § 16's administrative density.
 *
 * A LIST ITEM IS FOR A THING YOU ACT ON; A TABLE IS FOR A SET YOU COMPARE
 * ACROSS. That is the whole distinction, and it is why this component exists
 * beside `ContentListItem` rather than replacing it: the learner reads one
 * Lesson at a time down a spine, and an administrator reads forty accounts at
 * once down a column. Neither layout serves the other.
 *
 * A real `<table>`, not a grid of divs. Row and column association, the
 * caption, `aria-sort` and the reading order a screen reader announces are
 * things the element already carries; rebuilding them out of `role=` on
 * `<div>`s would be a second, weaker copy of a thing the platform does
 * correctly.
 *
 * Columns are DATA, passed in, so one table serves every administrative
 * screen and there is never a second nearly-identical one. A column says what
 * it is called, how it aligns, whether it can be sorted, and how to draw a
 * cell from a row — nothing about colour, spacing or borders, which are this
 * file's own.
 */

export interface DataTableColumn<Row> {
  /** Stable id — the sort key, and React's key for the cell. */
  id: string;
  header: string;
  /**
   * `end` for columns of numbers, which also take `numeric-inline` so digits
   * line up down the column (§ 14).
   */
  align?: 'start' | 'end';
  /** `min` holds the column to its content — chips, counts, row actions. */
  width?: 'auto' | 'min';
  sortable?: boolean;
  cell: (row: Row) => ReactNode;
  /**
   * The one column that NAMES the row. It takes `body` where the rest take
   * `body-sm`, and its cell is a `<th scope="row">`, so a screen reader
   * announces "Artur Marchi, Função, Administrador" rather than reading six
   * unattached values.
   */
  rowHeader?: boolean;
}

export interface DataTableSort {
  columnId: string;
  direction: 'asc' | 'desc';
}

interface DataTableProps<Row> {
  /**
   * The table's accessible name. Rendered as a `<caption>` — visually hidden
   * by default, because the screen's own heading normally says it out loud
   * already, and a table with no name at all is unusable by a screen reader.
   */
  caption: string;
  columns: readonly DataTableColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  sort?: DataTableSort;
  /** Called with the column id; the caller decides how the direction flips. */
  onSortChange?: (columnId: string) => void;
  /** What stands in for the body when there is nothing to show. */
  empty?: ReactNode;
  /**
   * A page is loading. The rows already on screen stay put and dim rather
   * than being replaced by a spinner: a table that empties itself between
   * pages makes the whole screen jump on every click.
   */
  busy?: boolean;
  testId?: string;
  /**
   * The test id each row carries. Named by the caller because a row is a
   * DOMAIN object — a Lesson, an account — and a suite that reads
   * `lesson-row` is asserting about Lessons, not about tables.
   */
  rowTestId?: string;
}

function SortAffordance({ direction }: { direction: 'asc' | 'desc' | undefined }) {
  if (!direction) return <span className={styles.sortIdle} aria-hidden="true" />;
  return (
    <span className={styles.sortMark} aria-hidden="true">
      <Icon name={direction === 'asc' ? 'arrow-up' : 'arrow-down'} size={16} />
    </span>
  );
}

export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  sort,
  onSortChange,
  empty,
  busy = false,
  testId,
  rowTestId = 'data-row',
}: DataTableProps<Row>) {
  return (
    <div className={styles.frame} data-busy={busy || undefined}>
      {/*
        The table scrolls INSIDE this container rather than widening the page
        (§ 14). `tabindex` and a role make the scroll region reachable by
        keyboard — a pointer can drag it, and without this a keyboard user
        could not read the columns past the fold.
      */}
      <div className={styles.scroll} tabIndex={0} role="group" aria-label={caption}>
        <table className={styles.table} data-testid={testId}>
          <caption>
            <VisuallyHidden>{caption}</VisuallyHidden>
          </caption>
          <thead>
            <tr>
              {columns.map((column) => {
                const sorted = sort?.columnId === column.id ? sort.direction : undefined;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    className={styles.head}
                    data-align={column.align ?? 'start'}
                    data-width={column.width ?? 'auto'}
                    aria-sort={
                      column.sortable
                        ? sorted === 'asc'
                          ? 'ascending'
                          : sorted === 'desc'
                            ? 'descending'
                            : 'none'
                        : undefined
                    }
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        className={`${styles.sortButton} type-label`}
                        onClick={() => onSortChange(column.id)}
                      >
                        {column.header}
                        <SortAffordance direction={sorted} />
                      </button>
                    ) : (
                      <span className="type-label">{column.header}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)} className={styles.row} data-testid={rowTestId}>
                {columns.map((column) =>
                  column.rowHeader ? (
                    <th
                      key={column.id}
                      scope="row"
                      className={styles.cell}
                      data-align={column.align ?? 'start'}
                      data-width={column.width ?? 'auto'}
                      data-row-header="true"
                    >
                      {column.cell(row)}
                    </th>
                  ) : (
                    <td
                      key={column.id}
                      className={styles.cell}
                      data-align={column.align ?? 'start'}
                      data-width={column.width ?? 'auto'}
                    >
                      {column.cell(row)}
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 ? (
        <div className={styles.empty}>
          {empty ?? (
            <Text as="p" variant="body-sm" tone="muted">
              Nada para mostrar.
            </Text>
          )}
        </div>
      ) : null}
    </div>
  );
}
